/**
 * Top 10 fleet assets table for the Overview page (PR 2 of dashboard
 * standards).
 *
 * Three-mode metric selector lives in the card header:
 *   - Open alerts (default)
 *   - Spend
 *   - Consumption drift
 *
 * The "Top alert station" column is populated by the backend as of
 * PR 5.5 (the per-asset top-alert-station CTE). Vehicles with no
 * resolvable station alerts in the period collapse to an em-dash;
 * the backend returns null in that case.
 *
 * Each registration cell links to the per-vehicle deep-dive page at
 * `/app/vehicle/:asset_token` (PR 5.5).
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useOverviewTopAssets } from '@/api/hooks/useOverview';
import type { Period } from '@/lib/timePeriod';
import type { TopAssetsSortBy, TopAssetsCategory } from '@/types/overview';
import { cn } from '@/lib/utils';

const SORT_OPTIONS: { value: TopAssetsSortBy; label: string; metricHeader: string }[] = [
  { value: 'alert_count', label: 'Open alerts', metricHeader: 'Alerts' },
  { value: 'spend', label: 'Spend', metricHeader: 'Spend' },
  { value: 'consumption_drift_pct', label: 'Consumption drift', metricHeader: 'Drift vs baseline' },
];

const CATEGORY_OPTIONS: { value: TopAssetsCategory; label: string }[] = [
  { value: 'fuel', label: 'Fuel' },
  { value: 'oil', label: 'Oil' },
  { value: 'toll', label: 'Toll' },
];

export interface TopAssetsTableProps {
  period: Period;
}

export function TopAssetsTable({ period }: TopAssetsTableProps): JSX.Element {
  const [category, setCategory] = useState<TopAssetsCategory>('fuel');
  const [sortBy, setSortBy] = useState<TopAssetsSortBy>('alert_count');
  const { data, isLoading, error } = useOverviewTopAssets(period, sortBy, category, 10);

  // Consumption drift is a fuel-only metric (litres + odometer) — hide it for
  // oil/toll and fall back to Open alerts if it was selected.
  const sortOptions =
    category === 'fuel'
      ? SORT_OPTIONS
      : SORT_OPTIONS.filter((o) => o.value !== 'consumption_drift_pct');

  const handleCategory = (next: TopAssetsCategory): void => {
    setCategory(next);
    if (next !== 'fuel' && sortBy === 'consumption_drift_pct') {
      setSortBy('alert_count');
    }
  };

  const metricHeader =
    SORT_OPTIONS.find((o) => o.value === sortBy)?.metricHeader ?? 'Metric';

  return (
    <Card>
      <CardHeader className="flex flex-col gap-2 pb-2 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <CardTitle className="text-sm">Top 10 fleet assets</CardTitle>
          <div className="inline-flex items-center gap-1 rounded-md border bg-background p-1">
            {CATEGORY_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                size="sm"
                variant={opt.value === category ? 'default' : 'outline'}
                onClick={() => handleCategory(opt.value)}
                aria-pressed={opt.value === category}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </div>
        <div className="inline-flex items-center gap-1 rounded-md border bg-background p-1">
          {sortOptions.map((opt) => (
            <Button
              key={opt.value}
              size="sm"
              variant={opt.value === sortBy ? 'default' : 'outline'}
              onClick={() => setSortBy(opt.value)}
              aria-pressed={opt.value === sortBy}
            >
              {opt.label}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto px-0">
        {error ? (
          <div className="px-6 py-4 text-sm text-destructive">
            Failed to load top assets: {(error as Error).message}
          </div>
        ) : isLoading || !data ? (
          <div className="space-y-2 px-6 pb-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-7 w-full" />
            ))}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Registration</TableHead>
                <TableHead>Make / Model</TableHead>
                <TableHead className="text-right">{metricHeader}</TableHead>
                <TableHead>Top alert station</TableHead>
                <TableHead>Last alert</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.assets.map((a) => (
                <TableRow key={a.asset_token}>
                  <TableCell className="font-mono text-xs">
                    <Link
                      to={`/vehicle/${a.asset_token}`}
                      className="text-foreground hover:underline"
                    >
                      {a.registration ?? '—'}
                    </Link>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {[a.make, a.model].filter(Boolean).join(' ') ||
                      (a.is_orphan ? (
                        <span
                          className="italic text-muted-foreground/70"
                          title="Seen only in the transaction/toll stream — no asset-master record, so no make/model is available."
                        >
                          Unmatched plate
                        </span>
                      ) : (
                        '—'
                      ))}
                  </TableCell>
                  <TableCell
                    className={cn(
                      'text-right font-medium tabular-nums',
                      sortBy === 'consumption_drift_pct' && a.metric_value > 0 && 'text-red-700 dark:text-red-400',
                    )}
                  >
                    {a.metric_label}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {a.primary_station ?? '—'}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {a.last_alert_at
                      ? new Date(a.last_alert_at).toLocaleString('en-ZA', {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })
                      : '—'}
                  </TableCell>
                </TableRow>
              ))}
              {data.assets.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center text-sm text-muted-foreground"
                  >
                    No matching assets in this period.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

export default TopAssetsTable;
